import { makeStyles } from 'tss-react/mui';
import { getBrandingColorPalette } from '../../../base/styles';
// Design System Constants - White & Blue Theme
// Refined spacing scale for better visual rhythm
const SPACING = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 40
};
// Consistent border radius scale
const RADIUS = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    full: '50%'
};
// Refined shadow system for depth
const SHADOWS = {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 2px 4px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.08)',
    lg: '0 4px 6px rgba(0, 0, 0, 0.07), 0 8px 16px rgba(0, 0, 0, 0.1)',
    xl: '0 8px 12px rgba(0, 0, 0, 0.08), 0 16px 24px rgba(0, 0, 0, 0.12)',
    primary: '0 4px 12px rgba(75, 103, 144, 0.25)',
    primaryHover: '0 6px 16px rgba(75, 103, 144, 0.2)',
    selected: '0 0 0 3px rgba(75, 103, 144, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1)'
};
// Smooth, consistent transitions
const TRANSITIONS = {
    fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '180ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '300ms cubic-bezier(0.34, 1.56, 0.64, 1)'
};
// Typography scale
const TYPOGRAPHY = {
    xs: 11,
    sm: 12,
    base: 14,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24
};
export const useStyles = makeStyles()(theme => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    // Get primary color from interfaceConfig if applyMeetingSettings is enabled, otherwise use default
    const primaryColor = (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings)
        ? (_a = getBrandingColorPalette(interfaceConfig)) === null || _a === void 0 ? void 0 : _a.primary
        : '#395071';
    // Dynamic COLORS object with calculated primary color
    const COLORS = {
        // Primary Blue Theme - dynamically calculated
        primary: primaryColor,
        primaryHover: '#3a5473',
        primaryActive: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_b = getBrandingColorPalette(interfaceConfig)) === null || _b === void 0 ? void 0 : _b.primary : '#2d4159',
        primaryLight: '#d9e7f8',
        primaryLighter: '#eff6ff',
        primaryDark: '#2d4159',
        colororgage: theme,
        // Success (Apply button) - Using secondary green
        success: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_c = getBrandingColorPalette(interfaceConfig)) === null || _c === void 0 ? void 0 : _c.secondary : '#1acc8d',
        successHover: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_d = getBrandingColorPalette(interfaceConfig)) === null || _d === void 0 ? void 0 : _d.secondary : '#15b37a',
        successActive: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_e = getBrandingColorPalette(interfaceConfig)) === null || _e === void 0 ? void 0 : _e.secondary : '#129a68',
        // Danger (Delete button) - Using accent2 red
        danger: '#dc3c4e',
        dangerHover: '#c5303f',
        dangerActive: '#ae2835',
        // Background colors
        bgPrimary: '#fff',
        bgSecondary: '#f8f9fa',
        bgTertiary: '#e9ecef',
        bgHover: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_f = getBrandingColorPalette(interfaceConfig)) === null || _f === void 0 ? void 0 : _f.accent2 : '#f1f3f5',
        bgActive: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_g = getBrandingColorPalette(interfaceConfig)) === null || _g === void 0 ? void 0 : _g.accent2 : '#e9ecef',
        bgPage: '#eff6ff',
        // Text colors with proper hierarchy
        textPrimary: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_h = getBrandingColorPalette(interfaceConfig)) === null || _h === void 0 ? void 0 : _h.accent1 : '#ffffff',
        textSecondary: '#5d769b',
        textTertiary: '#6c7a8f',
        textMuted: '#9aa5b8',
        textWhite: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_j = getBrandingColorPalette(interfaceConfig)) === null || _j === void 0 ? void 0 : _j.accent1 : '#F2F8FF',
        // Borders with refined opacity
        border: '#d1e3f6',
        borderLight: '#e5eef9',
        borderHover: primaryColor,
        borderActive: '#3a5473',
        borderFocus: primaryColor
    };
    return {
        // Main Dialog Container - Refined spacing and alignment
        dialogModern: {
            display: 'flex',
            flexDirection: 'row',
            gap: 0,
            padding: 0,
            // minHeight: '50vh',
            maxHeight: 'calc(95vh - 140px)',
            background: COLORS.bgPrimary,
            overflow: 'auto',
            width: '100%',
            '@media (max-width: 1024px)': {
                maxHeight: 'calc(95vh - 100px)'
            },
            '@media (max-width: 750px)': {
                flexDirection: 'column',
                minHeight: 'auto',
                maxHeight: 'calc(95vh - 140px)'
            },
            '@media (max-width: 640px)': {
                maxHeight: 'calc(95vh - 120px)',
                minHeight: 'auto'
            },
            '@media screen and (max-width: 767px) and (orientation: landscape)': {
                flexDirection: 'row',
                height: 'unset !important'
            },
            '@media (max-width: 480px)': {
                maxHeight: 'calc(100vh - 80px)',
                minHeight: 'auto'
            }
        },
        // Left Section: Preview & Controls - Refined spacing
        leftSection: {
            display: 'flex',
            flexDirection: 'column',
            flex: '0 0 58%',
            gap: SPACING.xl,
            padding: `${SPACING.xxl}px ${SPACING.xl}px`,
            borderRight: `1px solid ${COLORS.borderLight}`,
            minWidth: 0,
            justifyContent: 'flex-start',
            '@media (max-width: 1024px)': {
                flex: '0 0 55%',
                padding: `${SPACING.xl}px ${SPACING.lg}px`,
                gap: SPACING.lg
            },
            '@media (max-width: 750px)': {
                flex: '1 1 auto',
                width: '100%',
                borderRight: 'none',
                borderBottom: `1px solid ${COLORS.borderLight}`,
                padding: `${SPACING.xl}px ${SPACING.lg}px`,
                gap: SPACING.lg
            },
            '@media (max-width: 640px)': {
                padding: `${SPACING.lg}px ${SPACING.md}px`,
                gap: SPACING.md,
                maxWidth: '100%'
            },
            '@media (max-width: 480px)': {
                padding: `${SPACING.md}px ${SPACING.sm}px`,
                gap: SPACING.sm + 2
            },
            '@media screen and (max-width: 767px) and (orientation: landscape)': {
                width: '5000px'
            }
        },
        // Preview Container - Better aspect ratio handling
        previewContainer: {
            position: 'relative',
            width: '100%',
            flex: '1 1 auto',
            height: 'auto',
            minHeight: 0,
            maxHeight: '100%',
            display: 'flex',
            alignItems: 'stretch',
            '@media (max-width: 1024px)': {
                minHeight: 0,
                maxHeight: '100%'
            },
            '@media (max-width: 900px)': {
                minHeight: 0,
                maxHeight: '100%'
            },
            '@media (max-width: 640px)': {
                minHeight: 240,
                maxHeight: 'auto'
            },
            '@media (max-width: 600px)': {
                minHeight: 200,
                maxHeight: 260
            },
            '@media (max-width: 480px)': {
                minHeight: 200,
                maxHeight: 260,
                width: '100%'
            }
        },
        preview: {
            width: '100%',
            height: '100%',
            minHeight: 'inherit',
            borderRadius: RADIUS.lg,
            overflow: 'hidden',
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.borderLight}`,
            boxShadow: SHADOWS.md,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        previewVideo: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            '@media (min-width: 601px)': {
                maxHeight: '100%'
            },
            '@media (max-width: 600px)': {
                height: '100%',
                minHeight: 200,
                maxHeight: 260
            }
        },
        previewLoader: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            background: COLORS.bgSecondary,
            borderRadius: RADIUS.lg,
            border: `1px solid ${COLORS.borderLight}`
        },
        loadingText: {
            marginTop: SPACING.md,
            color: COLORS.textTertiary,
            fontSize: TYPOGRAPHY.base,
            fontWeight: 500,
            letterSpacing: '0.01em'
        },
        previewPlaceholder: {
            height: '100%',
            width: '100%',
            background: COLORS.bgSecondary,
            borderRadius: RADIUS.lg,
            border: `2px dashed ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        placeholderContent: {
            textAlign: 'center',
            color: COLORS.textSecondary,
            padding: SPACING.xl,
            '& p': {
                margin: `0 0 ${SPACING.sm}px 0`,
                fontSize: TYPOGRAPHY.base,
                fontWeight: 500,
                lineHeight: 1.5
            }
        },
        placeholderHint: {
            fontSize: TYPOGRAPHY.sm,
            color: COLORS.textTertiary,
            fontWeight: 400,
            lineHeight: 1.5
        },
        // Controls Section - Refined spacing and alignment
        controlsSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.lg,
            flexShrink: 0
        },
        actionButtons: {
            display: 'flex',
            gap: SPACING.md,
            alignItems: 'stretch',
            width: '100%'
        },
        applyButton: {
            flex: '1 1 0',
            minHeight: 48,
            padding: `${SPACING.md + 4}px ${SPACING.lg}px`,
            background: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_k = getBrandingColorPalette(interfaceConfig)) === null || _k === void 0 ? void 0 : _k.primary : '#4b6790',
            color: COLORS.textWhite,
            border: 'none',
            borderRadius: RADIUS.md,
            fontSize: TYPOGRAPHY.base,
            fontWeight: 600,
            letterSpacing: '0.01em',
            cursor: 'pointer',
            transition: `all ${TRANSITIONS.base}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: SHADOWS.sm,
            touchAction: 'manipulation',
            '&:hover': {
                background: COLORS.successHover,
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.md
            },
            '&:active': {
                background: COLORS.successActive,
                transform: 'translateY(0)',
                boxShadow: SHADOWS.sm
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primaryLight}`,
                outlineOffset: 2
            },
            '&:disabled': {
                background: COLORS.bgTertiary,
                color: COLORS.textMuted,
                cursor: 'not-allowed',
                transform: 'none',
                boxShadow: SHADOWS.none,
                opacity: 0.6
            },
            '@media (max-width: 640px)': {
                minHeight: 40,
                padding: `${SPACING.sm + 2}px ${SPACING.md}px`,
                fontSize: TYPOGRAPHY.sm
            },
            '@media (max-width: 480px)': {
                minHeight: 36,
                padding: `${SPACING.sm}px ${SPACING.sm + 2}px`,
                fontSize: TYPOGRAPHY.sm
            }
        },
        cancelButton: {
            flex: '1 1 0',
            minHeight: 48,
            padding: `${SPACING.md + 4}px ${SPACING.lg}px`,
            background: COLORS.bgHover,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_l = getBrandingColorPalette(interfaceConfig)) === null || _l === void 0 ? void 0 : _l.primary : '#000',
            border: `1.5px solid ${COLORS.border}`,
            borderRadius: RADIUS.md,
            fontSize: TYPOGRAPHY.base,
            fontWeight: 500,
            letterSpacing: '0.01em',
            cursor: 'pointer',
            transition: `all ${TRANSITIONS.base}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'manipulation',
            '&:hover': {
                borderColor: COLORS.borderHover,
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.sm
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: SHADOWS.none
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primaryLight}`,
                outlineOffset: 2,
                borderColor: COLORS.borderFocus
            },
            '@media (max-width: 640px)': {
                minHeight: 40,
                padding: `${SPACING.sm + 2}px ${SPACING.md}px`,
                fontSize: TYPOGRAPHY.sm
            },
            '@media (max-width: 480px)': {
                minHeight: 36,
                padding: `${SPACING.sm}px ${SPACING.sm + 2}px`,
                fontSize: TYPOGRAPHY.sm
            }
        },
        checkboxesContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.md + 2,
            paddingTop: SPACING.xs
        },
        mirrorCheckbox: {
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.md,
            cursor: 'pointer',
            userSelect: 'none',
            padding: `${SPACING.xs}px 0`,
            touchAction: 'manipulation',
            '& input[type="checkbox"]': {
                width: 18,
                height: 18,
                minWidth: 18,
                minHeight: 18,
                cursor: 'pointer',
                accentColor: COLORS.primary,
                flexShrink: 0,
                margin: 0
            },
            '& label': {
                fontSize: TYPOGRAPHY.base,
                color: COLORS.textPrimary,
                cursor: 'pointer',
                fontWeight: 400,
                lineHeight: 1.5,
                flex: 1,
                userSelect: 'none'
            },
            '&:hover': {
                '& label': {
                    color: COLORS.textPrimary
                }
            },
            '&:focus-within': {
                '& label': {
                    color: COLORS.primary
                }
            },
            '@media (max-width: 640px)': {
                gap: SPACING.sm + 2,
                padding: `${SPACING.sm}px 0`,
                '& input[type="checkbox"]': {
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20
                },
                '& label': {
                    fontSize: TYPOGRAPHY.sm
                }
            },
            '@media (max-width: 480px)': {
                '& input[type="checkbox"]': {
                    width: 22,
                    height: 22,
                    minWidth: 22,
                    minHeight: 22
                }
            }
        },
        // Right Section: Background Grid - Refined layout
        rightSection: {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minWidth: 0,
            gap: SPACING.lg,
            padding: `${SPACING.xxl}px ${SPACING.xl}px ${SPACING.xl}px`,
            maxHeight: 'calc(85vh - 40px)',
            overflow: 'hidden',
            background: COLORS.textWhite,
            '@media (max-width: 1024px)': {
                maxHeight: 'calc(95vh - 100px)'
            },
            '@media (max-width: 750px)': {
                padding: `${SPACING.xl}px ${SPACING.lg}px`,
                maxHeight: 'none',
                gap: SPACING.md
            },
            '@media (max-width: 640px)': {
                padding: `${SPACING.lg}px ${SPACING.md}px`,
                gap: SPACING.md,
                maxHeight: 'calc(95vh - 300px)'
            },
            '@media screen and (max-width: 767px) and (orientation: landscape)': {
                overflowY: 'scroll'
            },
            '@media (max-width: 480px)': {
                padding: `${SPACING.md}px ${SPACING.sm}px`,
                gap: SPACING.sm + 2,
                maxHeight: 'calc(100vh - 280px)'
            }
        },
        sectionTitle: {
            fontSize: TYPOGRAPHY.lg,
            fontWeight: 600,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? `${(_m = getBrandingColorPalette(interfaceConfig)) === null || _m === void 0 ? void 0 : _m.primary} !important` : '#00000080 !important',
            margin: 0,
            padding: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.3,
            flexShrink: 0,
            '@media (max-width: 640px)': {
                fontSize: TYPOGRAPHY.md
            },
            '@media (max-width: 480px)': {
                fontSize: TYPOGRAPHY.base
            }
        },
        // Quick Actions Section: None, Blur, Add Custom
        quickActionsSection: {
            display: 'flex',
            gap: SPACING.md,
            flexShrink: 0,
            marginBottom: SPACING.lg,
            flexWrap: 'wrap',
            '@media (max-width: 640px)': {
                gap: SPACING.sm + 2,
                marginBottom: SPACING.md
            },
            '@media screen and (max-width: 767px) and (orientation: landscape)': {
                marginBottom: 'unset'
            },
            '@media (max-width: 480px)': {
                // flexDirection: 'column',
                gap: SPACING.sm
            }
        },
        quickActionBtn: {
            flex: '1 1 0',
            minWidth: 0,
            minHeight: 52,
            padding: `${SPACING.md}px ${SPACING.lg}px`,
            border: `1.5px solid ${COLORS.border}`,
            borderRadius: RADIUS.md,
            background: COLORS.bgPrimary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            transition: `all ${TRANSITIONS.base}`,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_o = getBrandingColorPalette(interfaceConfig)) === null || _o === void 0 ? void 0 : _o.accent1 : '#000',
            fontWeight: 500,
            touchAction: 'manipulation',
            '&:hover': {
                borderColor: COLORS.primary,
                background: COLORS.primaryLighter,
                color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_p = getBrandingColorPalette(interfaceConfig)) === null || _p === void 0 ? void 0 : _p.accent1 : '#000',
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.sm,
                '& > div': {
                    '& svg': {
                        fill: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_q = getBrandingColorPalette(interfaceConfig)) === null || _q === void 0 ? void 0 : _q.accent1 : COLORS.primary
                    }
                }
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: SHADOWS.xs,
                '& > div': {
                    '& svg': {
                        fill: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_r = getBrandingColorPalette(interfaceConfig)) === null || _r === void 0 ? void 0 : _r.accent1 : COLORS.primary
                    }
                }
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primaryLight}`,
                outlineOffset: 2,
                borderColor: COLORS.primary
            },
            '& > div': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: RADIUS.sm,
                background: COLORS.border,
                flexShrink: 0,
                transition: `all ${TRANSITIONS.base}`,
                '& svg': {
                    width: 16,
                    height: 16,
                    fill: COLORS.textPrimary
                }
            },
            '& span': {
                fontSize: TYPOGRAPHY.base,
                fontWeight: 500,
                whiteSpace: 'nowrap'
            },
            '@media (max-width: 640px)': {
                minHeight: 48,
                padding: `${SPACING.md}px ${SPACING.md}px`,
                gap: SPACING.xs + 2,
                '& > div': {
                    width: 24,
                    height: 24,
                    '& svg': {
                        width: 14,
                        height: 14
                    }
                },
                '& span': {
                    fontSize: TYPOGRAPHY.sm
                }
            },
            '@media (max-width: 480px)': {
                width: '100%',
                minHeight: 48,
                justifyContent: 'flex-start',
                padding: `${SPACING.md}px ${SPACING.lg}px`
            }
        },
        quickActionBtnActive: {
            borderColor: COLORS.primary,
            borderWidth: '2px',
            background: COLORS.primary,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_s = getBrandingColorPalette(interfaceConfig)) === null || _s === void 0 ? void 0 : _s.accent1 : '#ffffff',
            boxShadow: SHADOWS.selected,
            '& > div': {
                '& svg path': {
                    fill: COLORS.primary
                }
            },
            '&:hover': {
                background: COLORS.primaryHover,
                borderColor: COLORS.primaryHover,
                '& > div': {
                    background: COLORS.textWhite,
                    '& svg path': {
                        fill: COLORS.primaryHover
                    }
                }
            }
        },
        addCustomImageBtn: {
            flex: '1 1 0',
            minWidth: 0,
            minHeight: 52,
            padding: `${SPACING.md}px ${SPACING.lg}px`,
            border: `2px dashed ${COLORS.primary}`,
            borderRadius: RADIUS.md,
            background: COLORS.primaryLighter,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            transition: `all ${TRANSITIONS.base}`,
            color: COLORS.primary,
            fontWeight: 500,
            touchAction: 'manipulation',
            '&:hover': {
                borderStyle: 'solid',
                borderWidth: '2px',
                background: COLORS.primaryLight,
                color: COLORS.primary,
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.sm
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: SHADOWS.xs
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primaryLight}`,
                outlineOffset: 2,
                borderStyle: 'solid'
            },
            '& svg': {
                width: 20,
                height: 20,
                flexShrink: 0,
                transition: `transform ${TRANSITIONS.base}`
            },
            '&:hover svg': {
                transform: 'scale(1.1)'
            },
            '@media (max-width: 640px)': {
                minHeight: 48,
                padding: `${SPACING.md}px ${SPACING.md}px`,
                gap: SPACING.xs + 2,
                '& svg': {
                    width: 18,
                    height: 18
                }
            },
            '@media (max-width: 480px)': {
                width: '100%',
                minHeight: 48,
                justifyContent: 'flex-start',
                padding: `${SPACING.md}px ${SPACING.lg}px`
            }
        },
        addCustomImageText: {
            fontSize: TYPOGRAPHY.base,
            fontWeight: 500,
            letterSpacing: '0.01em',
            lineHeight: 1.4,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_t = getBrandingColorPalette(interfaceConfig)) === null || _t === void 0 ? void 0 : _t.accent1 : '#000000'
        },
        // Background Grid - Refined grid with better spacing
        backgroundsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: SPACING.md + 2,
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: SPACING.sm,
            paddingBottom: SPACING.lg,
            paddingTop: SPACING.xs,
            alignContent: 'start',
            WebkitOverflowScrolling: 'touch',
            // Custom scrollbar - refined
            '&::-webkit-scrollbar': {
                width: 8
            },
            '&::-webkit-scrollbar-track': {
                background: COLORS.bgSecondary,
                borderRadius: RADIUS.sm,
                margin: SPACING.xs
            },
            '&::-webkit-scrollbar-thumb': {
                background: COLORS.border,
                borderRadius: RADIUS.sm,
                transition: `background ${TRANSITIONS.base}`,
                '&:hover': {
                    background: COLORS.textTertiary
                }
            },
            '@media (max-width: 900px)': {
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: SPACING.md,
                paddingRight: SPACING.xs
            },
            '@media screen and (max-width: 767px) and (orientation: landscape)': {
                minHeight: '100px'
            },
            '@media (max-width: 640px)': {
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: SPACING.sm + 2,
                paddingRight: SPACING.xs,
                paddingBottom: SPACING.md
            },
            '@media (max-width: 480px)': {
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: SPACING.sm + 2,
                paddingRight: SPACING.xs
            }
        },
        backgroundItem: {
            position: 'relative',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.xs + 2,
            minWidth: 0
        },
        imageWrapper: {
            position: 'relative',
            borderRadius: RADIUS.md,
            overflow: 'hidden',
            transition: `transform ${TRANSITIONS.base}, box-shadow ${TRANSITIONS.base}`,
            boxShadow: SHADOWS.sm,
            background: COLORS.bgSecondary,
            aspectRatio: '4/3',
            width: '100%',
            touchAction: 'manipulation',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: SHADOWS.md
            },
            '&:hover $child': {
                opacity: 1
                // transform: 'translateX(10px)'
            },
            '@media (max-width: 640px)': {
                '&:active': {
                    transform: 'scale(0.98)',
                    boxShadow: SHADOWS.xs
                }
            }
        },
        backgroundImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            border: `2.5px solid ${COLORS.border}`,
            borderRadius: RADIUS.md,
            cursor: 'pointer',
            transition: `all ${TRANSITIONS.base}`,
            display: 'block',
            background: COLORS.bgSecondary,
            '&:hover': {
                borderColor: COLORS.borderHover,
                boxShadow: SHADOWS.sm
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primary}`,
                outlineOffset: 2,
                borderColor: COLORS.primary
            }
        },
        imageSelected: {
            borderColor: COLORS.primary,
            borderWidth: '3.5px',
            boxShadow: SHADOWS.selected,
            transform: 'scale(1.02)',
            '&:hover': {
                transform: 'scale(1.02) translateY(-2px)',
                boxShadow: SHADOWS.selected
            }
        },
        imageLabel: {
            fontSize: TYPOGRAPHY.sm,
            fontWeight: 500,
            color: (interfaceConfig === null || interfaceConfig === void 0 ? void 0 : interfaceConfig.applyMeetingSettings) ? (_u = getBrandingColorPalette(interfaceConfig)) === null || _u === void 0 ? void 0 : _u.primary : '#000',
            textAlign: 'center',
            padding: `${SPACING.xs}px 0`,
            marginTop: SPACING.xs,
            lineHeight: 1.4,
            minHeight: 20,
            letterSpacing: '0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '@media (max-width: 480px)': {
                fontSize: TYPOGRAPHY.xs,
                minHeight: 18
            }
        },
        // Custom Images - Consistent with built-in
        customImageItem: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.xs + 2,
            minWidth: 0
        },
        customImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            border: `2.5px solid ${COLORS.border}`,
            borderRadius: RADIUS.md,
            cursor: 'pointer',
            transition: `all ${TRANSITIONS.base}`,
            display: 'block',
            background: COLORS.bgSecondary,
            aspectRatio: '4/3',
            '&:hover': {
                borderColor: COLORS.borderHover,
                boxShadow: SHADOWS.sm
            },
            '&:focus-visible': {
                outline: `3px solid ${COLORS.primary}`,
                outlineOffset: 2,
                borderColor: COLORS.primary
            }
        },
        deleteBtn: {
            position: 'absolute',
            top: SPACING.sm,
            right: SPACING.sm,
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            border: 'none',
            borderRadius: RADIUS.full,
            background: COLORS.danger,
            color: COLORS.textWhite,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: `all ${TRANSITIONS.base}`,
            boxShadow: SHADOWS.md,
            zIndex: 10,
            padding: 0,
            touchAction: 'manipulation',
            '&:hover': {
                background: COLORS.dangerHover,
                transform: 'scale(1.1)',
                opacity: 1,
                boxShadow: SHADOWS.lg
            },
            '&:active': {
                background: COLORS.dangerActive,
                transform: 'scale(1.05)'
            },
            '&:focus-visible': {
                outline: `2px solid ${COLORS.danger}`,
                outlineOffset: 2,
                opacity: 1
            },
            '& svg': {
                width: 14,
                height: 14,
                flexShrink: 0,
                '& path': {
                    fill: 'currentColor'
                }
            },
            '@media (max-width: 640px)': {
                width: 20,
                height: 20,
                minWidth: 20,
                minHeight: 20,
                top: SPACING.sm,
                right: SPACING.sm,
                opacity: 0.9,
                '& svg': {
                    width: 16,
                    height: 16
                }
            },
            '@media (max-width: 480px)': {
                width: 18,
                height: 18,
                minWidth: 18,
                minHeight: 18,
                '& svg': {
                    width: 18,
                    height: 18
                }
            }
        },
        customImageItemHover: {
            '&:hover $child': {
                opacity: 1
            },
            '@media (max-width: 640px)': {
                '& $deleteBtn': {
                    opacity: 0.9
                }
            }
        },
        // Loading Indicator
        loadingIndicator: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            padding: SPACING.xl
        },
        // Hidden file input
        hiddenInput: {
            display: 'none',
            visibility: 'hidden',
            position: 'absolute',
            width: 0,
            height: 0
        },
        // Control buttons for None/Blur - Refined
        controlButtons: {
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.sm + 2,
            paddingTop: SPACING.xs
        },
        controlBtn: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm,
            padding: `${SPACING.sm + 2}px ${SPACING.md}px`,
            border: `1.5px solid ${COLORS.border}`,
            borderRadius: RADIUS.sm,
            background: COLORS.bgPrimary,
            color: COLORS.textPrimary,
            fontWeight: 500,
            fontSize: TYPOGRAPHY.sm,
            letterSpacing: '0.01em',
            cursor: 'pointer',
            transition: `all ${TRANSITIONS.base}`,
            minHeight: 40,
            touchAction: 'manipulation',
            '&:hover': {
                borderColor: COLORS.borderHover,
                background: COLORS.primaryLight,
                color: COLORS.primary,
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.xs
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: SHADOWS.none
            },
            '&:focus-visible': {
                outline: `2px solid ${COLORS.primary}`,
                outlineOffset: 2,
                borderColor: COLORS.borderFocus
            },
            '& svg': {
                width: 16,
                height: 16,
                flexShrink: 0
            },
            '@media (max-width: 640px)': {
                minHeight: 44,
                padding: `${SPACING.md}px ${SPACING.md}px`,
                fontSize: TYPOGRAPHY.base
            },
            '@media (max-width: 480px)': {
                minHeight: 40,
                padding: `${SPACING.sm + 4}px ${SPACING.sm}px`
            }
        },
        controlBtnActive: {
            borderColor: COLORS.primary,
            background: COLORS.primary,
            color: COLORS.textWhite,
            boxShadow: SHADOWS.sm,
            '&:hover': {
                background: COLORS.primaryHover,
                borderColor: COLORS.primaryHover,
                color: COLORS.textWhite,
                transform: 'translateY(-1px)',
                boxShadow: SHADOWS.md
            },
            '&:active': {
                background: COLORS.primaryActive,
                transform: 'translateY(0)'
            },
            '& svg path': {
                fill: 'currentColor'
            }
        }
    };
});
